import { useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Library,
  LifeBuoy,
  SlidersHorizontal,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import InclusionAcademicsSchoolsModal from "@/components/InclusionAcademicsSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import { BlockLink } from "@/components/DrilldownLink";
import {
  buildInclusionActionQueue,
  buildInclusionClosurePlan,
  buildInclusionHeroSummary,
  computeInclusionScore,
  fmt,
  formatBooks,
  pct,
} from "@/utils/waterSafetyDerived";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";

const buildInclusionGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--inf-warning) 0 ${Math.min(value, 100)}%, rgba(245,158,11,.14) ${Math.min(value, 100)}% 100%)`,
});

const buildDonutStyle = (pctValue, colorVar, trackColor = "rgba(239,68,68,.12)") => ({
  background: `radial-gradient(circle at center, white 0 54%, transparent 55%), conic-gradient(${colorVar} 0 ${Math.min(pctValue, 100)}%, ${trackColor} ${Math.min(pctValue, 100)}% 100%)`,
});

const getWaterSafetyBadge = (score) => {
  if (score >= 80) return "green";
  if (score >= 60) return "orange";
  return "red";
};

const getRiskBadge = (score) => {
  if (score >= 3) return "high";
  if (score >= 2) return "mid";
  return "";
};

const INCLUSION_KPI_MODAL_KEYS = {
  "Ramp Available": "ramp_available",
  "Dedicated Educator": "dedicated_educator",
  "Library Coverage": "library_coverage",
  "Life-Saving Equip": "life_saving",
};

const INCLUSION_KPI_COVERAGE = {
  ramp_available: (overview) => overview?.ramp_coverage,
  dedicated_educator: (overview) => overview?.special_educator_dedicated_pct,
  library_coverage: (overview) => overview?.library_coverage,
  life_saving: (overview) => overview?.life_saving_coverage,
};

const INCLUSION_KPI_MISSING = {
  ramp_available: (overview, rampNo) => rampNo,
  dedicated_educator: (overview) =>
    Math.max(0, (overview?.total_schools ?? 0) - (overview?.special_educator_dedicated ?? 0)),
  library_coverage: (overview) => Math.max(0, (overview?.total_schools ?? 0) - (overview?.library_yes ?? 0)),
  life_saving: (overview) => Math.max(0, (overview?.total_schools ?? 0) - (overview?.life_saving_yes ?? 0)),
};

const LegendItem = ({ tone, label, value }) => (
  <div className="inf-inclusion-legend-item">
    <span className="inf-inclusion-legend-left">
      <i className={`inf-inclusion-dot ${tone}`} />
      {label}
    </span>
    <b>{value}</b>
  </div>
);

const WaterSafetyInclusionAcademicsTab = ({
  overview,
  blockData,
  inclusionMetrics,
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  scopeLabel = "Pune District",
}) => {
  const [inclusionMetricModal, setInclusionMetricModal] = useState(null);

  if (!overview) {
    return (
      <div className="inf-empty-card">
        <AccessibilityFallback />
        <p>No inclusion & academic data available for the selected scope.</p>
      </div>
    );
  }

  const rampPct = overview.ramp_coverage ?? 0;
  const libraryPct = overview.library_coverage ?? 0;
  const inclusionGapPct = overview.inclusion_gap_pct ?? 0;
  const dedicatedPct = overview.special_educator_dedicated_pct ?? 0;
  const educatorCoveredPct = 100 - inclusionGapPct;

  const rampDist = inclusionMetrics?.ramp ?? [];
  const educatorDist = inclusionMetrics?.special_educator ?? [];
  const rampYes = rampDist.find((d) => d.name === "Available")?.value ?? overview.ramp_yes ?? 0;
  const rampNo = rampDist.find((d) => d.name === "Not Available")?.value ?? Math.max(0, (overview.total_schools ?? 0) - rampYes);
  const educatorYes =
    educatorDist.find((d) => d.name === "Available")?.value ??
    (overview.special_educator_dedicated ?? 0) + (overview.special_educator_cluster ?? 0);
  const educatorNo = educatorDist.find((d) => d.name === "Not Available")?.value ?? overview.special_educator_no ?? 0;

  const inclusionScore = computeInclusionScore(overview);
  const localHero = buildInclusionHeroSummary(overview, inclusionScore);
  const hero = {
    summary: guidance?.hero_summary ?? localHero.summary,
    action: guidance?.primary_action ?? localHero.action,
    readinessLabel: guidance?.readiness_label ?? localHero.readinessLabel,
    readinessTone: guidance?.readiness_tone ?? localHero.readinessTone,
  };
  const localActionQueue = buildInclusionActionQueue(overview, blockData, rampNo, educatorNo);
  const actionQueue = (guidance?.action_queue?.length ? guidance.action_queue : localActionQueue).map((action, index) => ({
    title: action.title,
    description: action.description,
    deadline: action.deadline_days ?? action.deadline ?? localActionQueue[index]?.deadline ?? 30,
  }));
  const closurePlan =
    guidance?.closure_plan?.length > 0
      ? guidance.closure_plan
      : buildInclusionClosurePlan(overview, rampNo, educatorNo);
  const educatorTotal = educatorYes + educatorNo || 1;
  const educatorCoveredDonutPct = (educatorYes / educatorTotal) * 100;

  const tableBlocks = [...(blockData ?? [])]
    .sort((a, b) => {
      const riskDiff = (b.risk_score ?? 0) - (a.risk_score ?? 0);
      if (riskDiff !== 0) return riskDiff;
      return (a.water_safety_index ?? 0) - (b.water_safety_index ?? 0);
    })
    .slice(0, 9);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openInclusionMetric = (metricName) => {
    const modalKey = INCLUSION_KPI_MODAL_KEYS[metricName];
    if (!modalKey) return;
    const missing = INCLUSION_KPI_MISSING[modalKey]?.(overview, rampNo) ?? 0;
    if (missing <= 0) return;
    setInclusionMetricModal(modalKey);
  };

  const inclusionKpiCards = [
    {
      key: "Ramp Available",
      tone: "green",
      icon: SlidersHorizontal,
      label: "Ramp Available",
      value: pct(rampPct),
      sub: `${fmt(overview.ramp_yes)} schools`,
    },
    {
      key: "Dedicated Educator",
      tone: "",
      icon: Users,
      label: "Dedicated Educator",
      value: pct(dedicatedPct),
      sub: `${fmt(overview.special_educator_dedicated)} schools directly staffed`,
    },
    {
      key: "Inclusion Gap",
      tone: "red",
      icon: UserPlus,
      label: "Inclusion Gap",
      value: pct(inclusionGapPct),
      sub: `${fmt(overview.special_educator_no)} schools`,
      static: true,
    },
    {
      key: "Library Coverage",
      tone: "purple",
      icon: Library,
      label: "Library Coverage",
      value: pct(libraryPct),
      sub: `${fmt(overview.library_yes)} schools`,
    },
    {
      key: "Life-Saving Equip",
      tone: "orange",
      icon: LifeBuoy,
      label: "Life-Saving Equip",
      value: pct(overview.life_saving_coverage ?? 0),
      sub: `${fmt(overview.life_saving_yes ?? 0)} schools`,
    },
  ];

  const funnelSteps = [
    { label: "Ramp access", value: rampPct, tone: "green" },
    { label: "Library support", value: libraryPct, tone: "purple" },
    { label: "Inclusion staffing", value: educatorCoveredPct, tone: "red" },
    { label: "Dedicated educator", value: dedicatedPct, tone: "orange" },
  ];

  return (
    <div className="inf-water-tab inf-inclusion-tab" data-testid="tab-inclusion-content" id="inf-inclusion-overview">
      <section className="inf-hero-card inf-hero-card-inclusion">
        <div className="inf-hero-layout">
          <div>
            <div className="inf-health-main">
              <div
                className="inf-inclusion-gauge"
                style={buildInclusionGaugeStyle(inclusionScore)}
                aria-label={`Inclusion readiness ${inclusionScore.toFixed(1)} percent`}
              >
                <div>
                  <strong>{inclusionScore.toFixed(1)}%</strong>
                  <span>Inclusion</span>
                </div>
              </div>
              <div className="inf-hero-copy">
                <h2>Inclusion & Academic Readiness Command Centre</h2>
                <p>{hero.summary}</p>
                <div className="inf-status-row">
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant={hero.readinessTone}
                    onClick={inclusionScore < 90 ? () => scrollTo("inf-inclusion-actions") : undefined}
                    ariaLabel={
                      inclusionScore < 90
                        ? `${hero.readinessLabel} — view inclusion action queue`
                        : undefined
                    }
                  >
                    {hero.readinessLabel}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="blue"
                    onClick={educatorNo > 0 ? () => openInclusionMetric("Dedicated Educator") : undefined}
                    ariaLabel={
                      educatorNo > 0
                        ? `${fmt(overview.special_educator_no)} schools in inclusion gap — view school list`
                        : undefined
                    }
                  >
                    {fmt(overview.special_educator_no)} schools in inclusion gap
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="green"
                    onClick={
                      (overview.total_schools ?? 0) - (overview.library_yes ?? 0) > 0
                        ? () => openInclusionMetric("Library Coverage")
                        : undefined
                    }
                    ariaLabel={
                      (overview.total_schools ?? 0) - (overview.library_yes ?? 0) > 0
                        ? `${fmt(overview.library_yes)} schools with library coverage — view gaps`
                        : undefined
                    }
                  >
                    {fmt(overview.library_yes)} schools with library coverage
                  </HeroStatusChip>
                  <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                </div>
              </div>
            </div>
            <div className="inf-summary-note">
              <div className="inf-icon-box purple">
                <BookOpen size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{hero.action}</span>
              </div>
            </div>
          </div>

          <div className="inf-hero-metrics" aria-label="Executive inclusion KPIs">
            <article className="inf-hero-metric">
              <small>Ramp available</small>
              <strong>{pct(rampPct)}</strong>
              <span>{fmt(overview.ramp_yes)} schools enabled</span>
            </article>
            <article className="inf-hero-metric danger">
              <small>Inclusion gap</small>
              <strong>{pct(inclusionGapPct)}</strong>
              <span>{fmt(overview.special_educator_no)} schools need support</span>
            </article>
            <article className="inf-hero-metric">
              <small>Library coverage</small>
              <strong>{pct(libraryPct)}</strong>
              <span>{fmt(overview.library_yes)} schools covered</span>
            </article>
            <article className="inf-hero-metric warning">
              <small>Books per school</small>
              <strong>{fmt(Math.round(overview.avg_books_per_school ?? 0))}</strong>
              <span>{formatBooks(overview.total_books)} books total</span>
            </article>
          </div>
        </div>
      </section>

      <section className="inf-inclusion-kpi-grid" aria-label="Key inclusion and academic KPIs">
        {inclusionKpiCards.map((card) => {
          const Icon = card.icon;
          const modalKey = INCLUSION_KPI_MODAL_KEYS[card.key];
          const missing = modalKey ? INCLUSION_KPI_MISSING[modalKey]?.(overview, rampNo) ?? 0 : 0;
          const clickable = Boolean(modalKey && missing > 0 && !card.static);
          return (
            <article
              key={card.key}
              className={`inf-inclusion-kpi-card ${card.tone}${clickable ? " clickable" : ""}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `${card.label}: view schools missing coverage` : undefined}
              onClick={clickable ? () => openInclusionMetric(card.key) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openInclusionMetric(card.key);
                      }
                    }
                  : undefined
              }
              title={
                clickable ? `View ${fmt(missing)} schools missing ${card.label.toLowerCase()}` : undefined
              }
            >
              <div className="inf-inclusion-kpi-icon">
                <Icon size={20} />
              </div>
              <small>{card.label}</small>
              <strong>{card.value}</strong>
              <span>{card.sub}</span>
            </article>
          );
        })}
      </section>

      <div className="inf-section-title" id="inf-inclusion-diagnostics">
        <div>
          <h2>Access and Inclusion Diagnostics</h2>
          <p>
            Replaces separate isolated donuts with explainable status cards, count visibility, and next-action
            interpretation.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-inclusion-actions")}>
          View action queue →
        </button>
      </div>

      <section className="inf-diagnostic-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Physical Accessibility: Ramp Coverage</h3>
              <p>
                Most schools are physically accessible, but {fmt(rampNo)} schools still need ramp validation or
                construction closure.
              </p>
            </div>
            <span className="inf-pill inf-pill-green">Strong</span>
          </div>
          <div className="inf-inclusion-donut-card">
            <div
              className="inf-inclusion-donut"
              style={buildDonutStyle(rampPct, "var(--inf-accent)", "var(--inf-danger)")}
            >
              <div>
                <strong>{pct(rampPct)}</strong>
                <span>Ramp</span>
              </div>
            </div>
            <div className="inf-inclusion-legend-list">
              <LegendItem tone="" label="Available" value={fmt(rampYes)} />
              <LegendItem tone="red" label="Not Available" value={fmt(rampNo)} />
              <LegendItem tone="purple" label="Ramp exceptions" value={fmt(rampNo)} />
            </div>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Special Educator Availability</h3>
              <p>
                Direct dedicated educator coverage is low, while {fmt(educatorNo)} schools are marked as having an
                inclusion gap.
              </p>
            </div>
            <span className={`inf-pill ${inclusionGapPct > 30 ? "inf-pill-red" : inclusionGapPct > 15 ? "inf-pill-amber" : "inf-pill-green"}`}>
              {inclusionGapPct > 30 ? "Critical" : inclusionGapPct > 15 ? "Watch" : "Stable"}
            </span>
          </div>
          <div className="inf-inclusion-donut-card">
            <div
              className="inf-inclusion-donut"
              style={buildDonutStyle(educatorCoveredDonutPct, "var(--inf-accent)", "var(--inf-danger)")}
            >
              <div>
                <strong>{pct(educatorCoveredDonutPct)}</strong>
                <span>Covered</span>
              </div>
            </div>
            <div className="inf-inclusion-legend-list">
              <LegendItem tone="" label="Available / Cluster supported" value={fmt(educatorYes)} />
              <LegendItem tone="red" label="Not Available" value={fmt(educatorNo)} />
              <LegendItem
                tone="purple"
                label="Dedicated educator reported"
                value={`${fmt(overview.special_educator_dedicated)} schools`}
              />
            </div>
          </div>
        </article>
      </section>

      <section className="inf-inclusion-grid-3">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Inclusion Readiness Funnel</h3>
              <p>Shows how facilities convert into true inclusive readiness.</p>
            </div>
            <span className="inf-pill inf-pill-amber">Gap view</span>
          </div>
          <div className="inf-funnel">
            {funnelSteps.map((step) => (
              <div className="inf-funnel-step" key={step.label}>
                <div className="inf-funnel-label">{step.label}</div>
                <div className="inf-funnel-track">
                  <div
                    className={`inf-funnel-bar ${step.tone}`}
                    style={{ width: `${Math.min(step.value, 100)}%` }}
                  />
                </div>
                <div className="inf-funnel-value">{pct(step.value)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Academic Resource Readiness</h3>
              <p>Library availability is strong, so the next decision should focus on quality and usage.</p>
            </div>
            <span className="inf-pill inf-pill-purple">Library</span>
          </div>
          <div className="inf-book-card">
            <div className="inf-book-metric">
              <small>Library Coverage</small>
              <strong>{pct(libraryPct)}</strong>
              <div className="inf-book-progress">
                <i style={{ width: `${Math.min(libraryPct, 100)}%` }} />
              </div>
            </div>
            <div className="inf-book-metric">
              <small>Total Books</small>
              <strong>{formatBooks(overview.total_books)}</strong>
            </div>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Decision Signal</h3>
              <p>The key issue is not infrastructure availability, but inclusion staffing depth.</p>
            </div>
            <span className="inf-pill inf-pill-red">Action</span>
          </div>
          <div className="inf-note-card">
            <div className="inf-icon-box orange">
              <AlertTriangle size={22} />
            </div>
            <div>
              <h4>{fmt(overview.special_educator_no)} schools require inclusion follow-up</h4>
              <p>
                Trigger block-level review for special educator availability, CWSN mapping, and support route
                assignment.
              </p>
            </div>
          </div>
          <div className="inf-note-card">
            <div className="inf-icon-box green">
              <CheckCircle2 size={22} />
            </div>
            <div>
              <h4>Ramp coverage is implementation-ready</h4>
              <p>Use the {fmt(rampNo)}-school exception list for quick closure and physical verification.</p>
            </div>
          </div>
        </article>
      </section>

      <div className="inf-section-title">
        <div>
          <h2>Block-Wise Infrastructure and Inclusion Summary</h2>
          <p>
            Risk scoring is surfaced with clear badges so officers can prioritize schools and blocks without
            scanning raw percentages.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-inclusion-actions")}>
          Assign owners →
        </button>
      </div>

      <section className="inf-two-column">
        <article className="inf-panel inf-table-card">
          <div className="inf-table-head">
            <div>
              <h3>Block-Wise Inclusion Summary</h3>
              <p>Sorted by risk score and water-safety readiness.</p>
            </div>
            <span className="inf-pill">Top risk blocks</span>
          </div>
          <div className="inf-table-wrap">
            <table className="inf-data-table inf-inclusion-table" aria-label="Block-wise inclusion summary">
              <thead>
                <tr>
                  <th>Block</th>
                  <th className="num">Schools</th>
                  <th className="num">Water Safety</th>
                  <th className="num">Tap Water</th>
                  <th className="num">Purification</th>
                  <th className="num">Ramp</th>
                  <th className="num">Library</th>
                  <th className="num">Risk</th>
                </tr>
              </thead>
              <tbody>
                {tableBlocks.map((block) => (
                  <tr key={block.block_name}>
                    <td>
                      <b>
                        <BlockLink blockCode={block.block_code}>{block.block_name}</BlockLink>
                      </b>
                    </td>
                    <td className="num">{fmt(block.total_schools)}</td>
                    <td className="num">
                      <span className={`inf-score-badge ${getWaterSafetyBadge(block.water_safety_index ?? 0)}`}>
                        {pct(block.water_safety_index, 1)}
                      </span>
                    </td>
                    <td className="num">{pct(block.tap_water_pct, 1)}</td>
                    <td className="num">{pct(block.purification_pct, 1)}</td>
                    <td className="num">{pct(block.ramp_pct, 1)}</td>
                    <td className="num">{pct(block.library_pct, 1)}</td>
                    <td className="num">
                      <span className={`inf-risk-badge ${getRiskBadge(block.risk_score ?? 0)}`}>
                        {block.risk_score ?? 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="inf-panel" id="inf-inclusion-actions">
          <div className="inf-panel-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Converts diagnostics into clear ownership and closure actions.</p>
            </div>
            <span className="inf-pill inf-pill-red">Priority</span>
          </div>
          <div className="inf-action-card">
            {actionQueue.map((action, index) => {
              const icons = [Users, SlidersHorizontal, Library, TrendingUp];
              const tones = ["red", "orange", "purple", "green"];
              const Icon = icons[index] ?? TrendingUp;
              return (
                <div className="inf-action-item" key={action.title}>
                  <div className={`inf-icon-box ${tones[index] ?? "green"}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                  <span className="inf-deadline">{action.deadline} days</span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <div className="inf-section-title">
        <div>
          <h2>30-Day Inclusion Closure Plan</h2>
          <p>A simple phased plan helps convert dashboard insights into trackable field execution.</p>
        </div>
      </div>

      <section className="inf-timeline">
        {closurePlan.map((step) => (
          <article className="inf-timeline-step" key={step.week}>
            <b>{step.week}</b>
            <strong>{step.title}</strong>
            <p>{step.description}</p>
          </article>
        ))}
      </section>

      <InclusionAcademicsSchoolsModal
        open={Boolean(inclusionMetricModal)}
        metric={inclusionMetricModal}
        onClose={() => setInclusionMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          inclusionMetricModal && INCLUSION_KPI_COVERAGE[inclusionMetricModal]
            ? INCLUSION_KPI_COVERAGE[inclusionMetricModal](overview)
            : null
        }
      />
    </div>
  );
};

const AccessibilityFallback = () => (
  <svg className="mx-auto mb-3 opacity-40" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="8" cy="7" r="4" />
    <path d="M4 21v-2a4 4 0 0 1 4-4h1" />
    <path d="M17 11v6" />
    <path d="M14 14h6" />
  </svg>
);

export default WaterSafetyInclusionAcademicsTab;
